import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import SVG from 'components/base/SVG';
import Tooltip from 'app/components/Tooltip';
import { addListener, removeListener, isOnElement } from 'utils/DOM/onOuterClick';
import {
  SearchRadiusOptions,
  PurchaseTimeWindowOptions,
  PropertyTypeOptions,
  InvestorTypeFilterOptions,
  MinPropertiesPurchasedOptions,
  AvgPurchasePriceOptions,
  searchSubjectSuggestions,
  setSubjectLabel,
  setSubject,
  clearSubject,
  setRecentSubjects,
  setInvestorFilter,
  searchInvestors,
  clearInvestorResults,
  selectSubjectLabel,
  selectSubjectSuggestions,
  selectSubjectSuggestionsQuery,
  selectRecentSubjects,
  selectSubject,
  selectSubjectComplete,
  selectSubjectPreselected,
  selectInvestorFilters,
  selectInvestorStale,
  selectLoading,
  suggestionToSubject,
} from 'data/investors';

import {
  getRecentSubjects,
  addRecentSubject,
  removeRecentSubject,
  clearRecentSubjects,
} from '../recentSubjects';
import Dropdown from './Dropdown';
import SubjectInput from './SubjectInput';
import css from './style.scss';

const SUGGEST_DEBOUNCE_MS = 300;
const MIN_SUGGEST_LENGTH = 3;

const Field = ({ label, className, children }) => (
  <div className={classNames(css.field, className)}>
    <span className={css.label}>{label}</span>
    {children}
  </div>
);

// Search + filter section — Figma nodes 291:9023, 452:33676 and 291:6050.
const SearchFilter = ({
  label,
  suggestions,
  suggestionsQuery,
  recentSubjects,
  subject,
  subjectComplete,
  subjectPreselected,
  loading,
  stale,
  filters,
  canSearch,
  searchSubjectSuggestions,
  searchInvestors,
  clearInvestorResults,
  setSubjectLabel,
  setSubject,
  clearSubject,
  setRecentSubjects,
  setInvestorFilter,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const timer = useRef(null);
  const moreRef = useRef(null);

  useEffect(() => {
    setRecentSubjects(getRecentSubjects(), true);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [setRecentSubjects]);

  useEffect(() => {
    if (!moreOpen) return undefined;

    const handleOuterClick = (ev) => {
      if (!isOnElement(ev, moreRef.current)) setMoreOpen(false);
    };

    addListener(handleOuterClick);

    return () => removeListener(handleOuterClick);
  }, [moreOpen]);

  // Auto-search, as on PropStream Search: an address pick or filter change searches once idle.
  // Gated on access so the locked screen (upsell overlay up) never fires a search (S5).
  useEffect(() => {
    if (!canSearch || !stale || !subjectComplete || loading) return;
    searchInvestors(subject, filters);
  }, [canSearch, stale, subjectComplete, loading, subject, filters, searchInvestors]);

  const handleFetch = (text) => {
    setSubjectLabel(text);

    if (timer.current) clearTimeout(timer.current);
    if ((text || '').trim().length < MIN_SUGGEST_LENGTH) return;

    timer.current = setTimeout(() => searchSubjectSuggestions(text), SUGGEST_DEBOUNCE_MS);
  };

  const handleSelect = (suggestion) => {
    const subject = suggestionToSubject(suggestion);
    if (!subject) return;

    setSubject(subject);
    setRecentSubjects(addRecentSubject(subject));

    // Picking a suggestion or a recent search is a commit, so run the search
    // immediately instead of making the user click Find. Only for a resolved
    // property, though: the CTA is likewise gated on `propertyId` (city/other
    // suggestions aren't searchable), and `loading` guards against firing a
    // second request over one already in flight. Pass the freshly built subject
    // rather than the `subject` prop, which redux hasn't updated yet this tick.
    // Access-gated too, so the locked screen can't search (S5).
    if (canSearch && subject.propertyId && !loading) searchInvestors(subject, filters);
  };

  const handleClear = () => {
    clearSubject();
  };

  const setFilter = (name) => (value) => setInvestorFilter(name, value);

  // Fires the search against propstream-core through the API middleware. Guarded
  // so a queued request can't be double-submitted from a second click.
  const handleFind = () => {
    // Entitlement gate: a primary holder who has not subscribed (and anyone else without access)
    // sees the locked screen behind the upsell and must not be able to run a search — the API
    // would refuse it anyway (S5); this stops the request client-side. `canSearch` is the route's
    // entitlement (hook-derived), so it can't disagree with the overlay.
    if (!canSearch || loading) return;

    // No resolved property (typed text no suggestion matched): no request. Any
    // results still on screen belong to the previous address, so they go too
    // (ATAI-491).
    if (!subjectComplete) {
      clearInvestorResults();
      return;
    }

    // A preselected subject was never clicked, so searching on it is the point
    // at which the user commits to it — that is when it joins the recents.
    if (subjectPreselected) setRecentSubjects(addRecentSubject(subject));

    searchInvestors(subject, filters);
  };

  // The CTA is disabled only while the address field is empty
  // any text enables it and it stays enabled, including while a search runs
  // (handleFind ignores clicks during `loading`, so nothing is double-submitted).
  const addressDisabled = !(label || '').trim();

  const findButton = (
    <button
      type="button"
      className={css.findButton}
      disabled={addressDisabled}
      onClick={handleFind}
    >
      <SVG icon="iconInvestorSearch" className={css.findButton__icon} />
      Find Investor
    </button>
  );

  // The Figma 1547:69550 callout explains the disabled CTA on hover, so it is
  // only attached while the button is disabled: an enabled button
  // never pops it.
  //
  // Offsets map the design's geometry onto the shared Tooltip, which centres
  // itself on its trigger: the 312px bubble sits with its arrow 25px in from
  // its own left edge, and xOffset slides that arrow onto the button (half the
  // bubble, less the arrow inset, less the 19px the design nudges it left of
  // the button's centre). yOffset trims the component's default 20px drop to
  // the 5px gap the design leaves under the button. The Tooltip's 22x22 close
  // box already matches Figma's Tooltips/Dismiss Button; only its glyph is
  // swapped for the design's thin 10x10 X (ATAI-292).
  const renderFindButton = () =>
    !addressDisabled ? (
      findButton
    ) : (
      <Tooltip
        position="bottom"
        title="Enter Property Address"
        description="Enter a property address to search. State, county, city and zip searches are not supported."
        width={312}
        arrowOffset={25}
        xOffset={112}
        yOffset={-6}
        closeIcon={<SVG icon="iconTooltipDismiss" className={css.tooltipDismissIcon} />}
      >
        <span className={css.findButtonWrap}>{findButton}</span>
      </Tooltip>
    );

  return (
    <div className={css.container}>
      <div className={css.searchGroup}>
        <span className={css.label}>Subject Property</span>
        <div className={css.searchRow}>
          <SubjectInput
            label={label}
            suggestions={suggestions}
            suggestionsQuery={suggestionsQuery}
            recentSubjects={recentSubjects}
            preselectedId={subjectPreselected && subject ? subject.id || null : null}
            onFetch={handleFetch}
            onSelect={handleSelect}
            onSubmit={handleFind}
            onClear={handleClear}
            onRemoveRecent={(key) => setRecentSubjects(removeRecentSubject(key))}
            onClearRecents={() => setRecentSubjects(clearRecentSubjects())}
          />
          {renderFindButton()}
        </div>
      </div>

      <div className={css.divider} />

      <div className={css.filterRow}>
        <Field label="Search Radius" className={css.radiusField}>
          <Dropdown
            value={filters.get('radius')}
            options={SearchRadiusOptions}
            onChange={setFilter('radius')}
            maxWidth={135}
            menuClassName={css.filterMenu}
          />
        </Field>
        <Field label="Purchase Time Window" className={css.windowField}>
          <Dropdown
            value={filters.get('timeWindow')}
            options={PurchaseTimeWindowOptions}
            onChange={setFilter('timeWindow')}
            maxWidth={139}
            menuClassName={css.filterMenu}
          />
        </Field>
        <Field label="Property Type" className={css.typeField}>
          <Dropdown
            value={filters.get('propertyType')}
            options={PropertyTypeOptions}
            onChange={setFilter('propertyType')}
            maxWidth={180}
            menuClassName={css.filterMenu}
          />
        </Field>
        <Field label="Investor Type" className={css.typeField}>
          <Dropdown
            multiple
            value={filters.get('investorTypes')}
            options={InvestorTypeFilterOptions}
            onChange={(next) => setInvestorFilter('investorTypes', next.toJS())}
            maxWidth={180}
            menuClassName={css.filterMenu}
          />
        </Field>

        <div className={css.more} ref={moreRef}>
          <button
            type="button"
            className={classNames(css.moreButton, { [css.moreButtonOpen]: moreOpen })}
            onClick={() => setMoreOpen(!moreOpen)}
          >
            <SVG
              icon={moreOpen ? 'iconInvestorFilterWhite' : 'iconInvestorFilter'}
              className={css.moreButton__icon}
            />
            More
          </button>
          {!moreOpen ? null : (
            <div className={css.morePanel}>
              <div className={css.morePanel__group}>
                <span className={css.label}>Average Purchase Price</span>
                {/* Min and Max are one segmented control in the design, so they
                    sit 2px apart and only round on the pair's outer edges. */}
                <div className={css.morePanel__row}>
                  <Dropdown
                    placeholder
                    className={classNames(css.morePanel__half, css.morePanel__halfStart)}
                    value={filters.get('avgPurchasePriceMin')}
                    options={[{ value: '', label: 'Min' }, ...AvgPurchasePriceOptions]}
                    onChange={setFilter('avgPurchasePriceMin')}
                  />
                  <Dropdown
                    placeholder
                    className={classNames(css.morePanel__half, css.morePanel__halfEnd)}
                    value={filters.get('avgPurchasePriceMax')}
                    options={[{ value: '', label: 'Max' }, ...AvgPurchasePriceOptions]}
                    onChange={setFilter('avgPurchasePriceMax')}
                  />
                </div>
              </div>
              <div className={css.morePanel__group}>
                <span className={css.label}>Min Properties Purchased</span>
                <Dropdown
                  value={filters.get('minPropertiesPurchased')}
                  options={MinPropertiesPurchasedOptions}
                  onChange={setFilter('minPropertiesPurchased')}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default connect(
  (state) => ({
    label: selectSubjectLabel(state),
    suggestions: selectSubjectSuggestions(state),
    suggestionsQuery: selectSubjectSuggestionsQuery(state),
    recentSubjects: selectRecentSubjects(state),
    subject: selectSubject(state),
    subjectComplete: selectSubjectComplete(state),
    subjectPreselected: selectSubjectPreselected(state),
    loading: selectLoading(state),
    stale: selectInvestorStale(state),
    filters: selectInvestorFilters(state),
  }),
  {
    searchSubjectSuggestions,
    searchInvestors,
    clearInvestorResults,
    setSubjectLabel,
    setSubject,
    clearSubject,
    setRecentSubjects,
    setInvestorFilter,
  }
)(SearchFilter);
